import { create } from 'zustand';
import { ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';
export type ToastPosition = 
  | 'top-left' 
  | 'top-right' 
  | 'bottom-left' 
  | 'bottom-right' 
  | 'top-center' 
  | 'bottom-center';

export interface ToastButton {
  label: string;
  onClick: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface ToastOptions {
  type?: ToastType;
  position?: ToastPosition;
  duration?: number;
  showCloseButton?: boolean;
  pauseOnHover?: boolean;
  customIcon?: ReactNode;
  customClassName?: string;
  style?: React.CSSProperties;
  buttons?: ToastButton[];
}

export interface ToastState {
  show: boolean;
  message: string | ReactNode;
  type: ToastType;
  position: ToastPosition;
  duration: number;
  showCloseButton: boolean;
  pauseOnHover: boolean;
  customIcon?: ReactNode;
  customClassName?: string;
  style?: React.CSSProperties;
  buttons?: ToastButton[];
}

interface ToastStore {
  toastState: ToastState;
  showToast: (message: string | ReactNode, options?: ToastOptions) => void;
  hideToast: () => void;
  showSuccess: (message: string | ReactNode, options?: Omit<ToastOptions, 'type'>) => void;
  showError: (message: string | ReactNode, options?: Omit<ToastOptions, 'type'>) => void;
  showInfo: (message: string | ReactNode, options?: Omit<ToastOptions, 'type'>) => void;
  showWarning: (message: string | ReactNode, options?: Omit<ToastOptions, 'type'>) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toastState: {
    show: false,
    message: '',
    type: 'success',
    position: 'top-right',
    duration: 3000,
    showCloseButton: false,
    pauseOnHover: true
  },

  showToast: (message: string | ReactNode, options: ToastOptions = {}) => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: true,
        message,
        type: options.type || 'success',
        position: options.position || 'top-right',
        duration: options.duration !== undefined ? options.duration : 3000,
        showCloseButton: options.showCloseButton || false,
        pauseOnHover: options.pauseOnHover !== false,
        customIcon: options.customIcon,
        customClassName: options.customClassName,
        style: options.style,
        buttons: options.buttons
      }
    }));
  },

  hideToast: () => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: false
      }
    }));
  },

  showSuccess: (message: string | ReactNode, options: Omit<ToastOptions, 'type'> = {}) => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: true,
        message,
        type: 'success',
        position: options.position || 'top-right',
        duration: options.duration !== undefined ? options.duration : 3000,
        showCloseButton: options.showCloseButton || false,
        pauseOnHover: options.pauseOnHover !== false,
        customIcon: options.customIcon,
        customClassName: options.customClassName,
        style: options.style,
        buttons: options.buttons
      }
    }));
  },

  showError: (message: string | ReactNode, options: Omit<ToastOptions, 'type'> = {}) => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: true,
        message,
        type: 'error',
        position: options.position || 'top-right',
        duration: options.duration !== undefined ? options.duration : 3000,
        showCloseButton: options.showCloseButton || false,
        pauseOnHover: options.pauseOnHover !== false,
        customIcon: options.customIcon,
        customClassName: options.customClassName,
        style: options.style,
        buttons: options.buttons
      }
    }));
  },

  showInfo: (message: string | ReactNode, options: Omit<ToastOptions, 'type'> = {}) => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: true,
        message,
        type: 'info',
        position: options.position || 'top-right',
        duration: options.duration !== undefined ? options.duration : 3000,
        showCloseButton: options.showCloseButton || false,
        pauseOnHover: options.pauseOnHover !== false,
        customIcon: options.customIcon,
        customClassName: options.customClassName,
        style: options.style,
        buttons: options.buttons
      }
    }));
  },

  showWarning: (message: string | ReactNode, options: Omit<ToastOptions, 'type'> = {}) => {
    set((state) => ({
      toastState: {
        ...state.toastState,
        show: true,
        message,
        type: 'warning',
        position: options.position || 'top-right',
        duration: options.duration !== undefined ? options.duration : 3000,
        showCloseButton: options.showCloseButton || false,
        pauseOnHover: options.pauseOnHover !== false,
        customIcon: options.customIcon,
        customClassName: options.customClassName,
        style: options.style,
        buttons: options.buttons
      }
    }));
  }
}));